import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

/**
 * Configuration read from the server's environment on every request and handed
 * to the client through _app.getInitialProps.
 *
 * Names here must NOT carry the NEXT_PUBLIC_ prefix. That prefix is what makes
 * Next.js substitute a value into the client bundle at build time, which ties
 * the value to the deployment that compiled it — changing it in the hosting
 * environment then has no effect until the next build, and a value missing at
 * build time stays `undefined` forever. Reading unprefixed vars server-side
 * instead means a change takes effect on the next request.
 *
 * Everything in here is serialized into the page HTML, so it is public. Secrets
 * belong in server-only code, not in this object.
 */
export type RuntimeConfig = {
  /**
   * Google Maps Embed API key. Null disables the venue map in favour of an
   * address-only card. The key travels in the embed iframe URL and is therefore
   * always visible to the client — restrict it by HTTP referrer in Google Cloud.
   */
  googleMapsApiKey: string | null;
};

export const emptyRuntimeConfig: RuntimeConfig = {
  googleMapsApiKey: null,
};

/**
 * Reads the runtime config from process.env. Server-side only — _app bundles
 * this module for the client too, where process.env holds no unprefixed vars
 * and every field would come back null.
 */
export function readRuntimeConfig(): RuntimeConfig {
  return {
    googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY || null,
  };
}

const RuntimeConfigContext = createContext<RuntimeConfig>(emptyRuntimeConfig);

export function RuntimeConfigProvider({
  value,
  children,
}: {
  value: RuntimeConfig;
  children: ReactNode;
}) {
  return (
    <RuntimeConfigContext.Provider value={value}>
      {children}
    </RuntimeConfigContext.Provider>
  );
}

export const useRuntimeConfig = () => useContext(RuntimeConfigContext);
